import type { SearchResultItem } from '@/store/slices/searchSlice';
import type { User, Vehicle, Booking } from '@/types';
import type { Driver } from '@/services/driverService';

// Helper function to highlight matching text
export const highlightText = (text: string, query: string): string => {
  if (!query) return text;
  const regex = new RegExp(`(${query})`, 'gi');
  return text.replace(regex, '<mark>$1</mark>');
};

// Helper function to check if text contains query
const containsQuery = (text: string, query: string): boolean => {
  return text.toLowerCase().includes(query.toLowerCase());
};

// Helper function to get matched fields
const getMatchedFields = (item: any, query: string, fields: string[]): string[] => {
  return fields.filter(field => {
    const value = getNestedValue(item, field);
    return value && containsQuery(String(value), query);
  });
};

// Helper function to get nested object values
const getNestedValue = (obj: any, path: string): any => {
  return path.split('.').reduce((current, key) => current?.[key], obj);
};

// Search users
export const searchUsers = (users: User[], query: string): SearchResultItem[] => {
  if (!query || query.length < 2) return [];
  
  const searchFields = [
    'personalInfo.firstName',
    'personalInfo.lastName',
    'personalInfo.email',
    'personalInfo.phone',
    'personalInfo.location',
    'userCode',
    'userType',
    'hostType',
    'verificationStatus',
    'accountStatus'
  ];
  
  return users
    .filter(user => {
      return searchFields.some(field => {
        const value = getNestedValue(user, field);
        return value && containsQuery(String(value), query);
      });
    })
    .map(user => ({
      id: user.id,
      type: 'user' as const,
      title: `${user.personalInfo.firstName} ${user.personalInfo.lastName}`,
      subtitle: `${user.userCode} • ${user.userType}`,
      description: user.personalInfo.email,
      path: `/users/management?highlight=${user.id}`,
      data: user,
      matchedFields: getMatchedFields(user, query, searchFields),
    }));
};

// Search vehicles
export const searchVehicles = (vehicles: Vehicle[], query: string): SearchResultItem[] => {
  if (!query || query.length < 2) return [];
  
  const searchFields = [
    'vehicleCode',
    'make',
    'model',
    'year',
    'licensePlate',
    'vin',
    'color',
    'type',
    'status',
    'location',
    'owner.name',
    'owner.phone'
  ];
  
  return vehicles
    .filter(vehicle => {
      return searchFields.some(field => {
        const value = getNestedValue(vehicle, field);
        return value && containsQuery(String(value), query);
      });
    })
    .map(vehicle => ({
      id: vehicle.id,
      type: 'vehicle' as const,
      title: `${vehicle.make} ${vehicle.model} ${vehicle.year}`,
      subtitle: `${vehicle.vehicleCode} • ${vehicle.licensePlate}`,
      description: `${vehicle.color} • ${vehicle.location}`,
      path: `/vehicles?highlight=${vehicle.id}`,
      data: vehicle,
      matchedFields: getMatchedFields(vehicle, query, searchFields),
    }));
};

// Search bookings
export const searchBookings = (bookings: Booking[], query: string): SearchResultItem[] => {
  if (!query || query.length < 2) return [];
  
  const searchFields = [
    'bookingCode',
    'status',
    'renterInfo.name',
    'renterInfo.email',
    'renterInfo.phone',
    'vehicleInfo.make',
    'vehicleInfo.model',
    'vehicleInfo.licensePlate',
    'pickupLocation',
    'dropoffLocation',
    'totalAmount'
  ];
  
  return bookings
    .filter(booking => {
      return searchFields.some(field => {
        const value = getNestedValue(booking, field);
        return value && containsQuery(String(value), query);
      });
    })
    .map(booking => ({
      id: booking.id,
      type: 'booking' as const,
      title: `Booking ${booking.bookingCode}`,
      subtitle: `${booking.renterInfo.name} • ${booking.status}`,
      description: `${booking.vehicleInfo.make} ${booking.vehicleInfo.model} • ₦${booking.totalAmount}`,
      path: `/bookings/all?highlight=${booking.id}`,
      data: booking,
      matchedFields: getMatchedFields(booking, query, searchFields),
    }));
};

// Search drivers
export const searchDrivers = (drivers: Driver[], query: string): SearchResultItem[] => {
  if (!query || query.length < 2) return [];
  
  const searchFields = [
    'driverCode',
    'personalInfo.firstName',
    'personalInfo.lastName',
    'personalInfo.email',
    'personalInfo.phone',
    'personalInfo.location',
    'licenseInfo.licenseNumber',
    'licenseInfo.state',
    'status',
    'verificationStatus',
    'currentVehicle.make',
    'currentVehicle.model',
    'currentVehicle.licensePlate'
  ];
  
  return drivers
    .filter(driver => {
      return searchFields.some(field => {
        const value = getNestedValue(driver, field);
        return value && containsQuery(String(value), query);
      });
    })
    .map(driver => ({
      id: driver.id,
      type: 'driver' as const,
      title: `${driver.personalInfo.firstName} ${driver.personalInfo.lastName}`,
      subtitle: `${driver.driverCode} • ${driver.status}`,
      description: `${driver.personalInfo.location} • ${driver.licenseInfo.licenseNumber}`,
      path: `/drivers/management?highlight=${driver.id}`,
      data: driver,
      matchedFields: getMatchedFields(driver, query, searchFields),
    }));
};

// Main search function
export const performGlobalSearch = (
  query: string,
  data: {
    users: User[];
    vehicles: Vehicle[];
    bookings: Booking[];
    drivers: Driver[];
  }
): SearchResultItem[] => {
  if (!query || query.length < 2) return [];
  
  const results: SearchResultItem[] = [];
  
  // Search each data type
  results.push(...searchUsers(data.users, query));
  results.push(...searchVehicles(data.vehicles, query));
  results.push(...searchBookings(data.bookings, query));
  results.push(...searchDrivers(data.drivers, query));
  
  // Sort results by relevance (more matched fields = higher relevance)
  return results.sort((a, b) => {
    const aRelevance = a.matchedFields.length;
    const bRelevance = b.matchedFields.length;
    
    if (aRelevance !== bRelevance) {
      return bRelevance - aRelevance;
    }
    
    // If same relevance, sort by type priority
    const typePriority = { user: 1, driver: 2, vehicle: 3, booking: 4 };
    return typePriority[a.type] - typePriority[b.type];
  });
};

// Search suggestions based on recent searches and popular queries
export const getSearchSuggestions = (recentSearches: string[], query: string): string[] => {
  if (!query) return recentSearches.slice(0, 5);
  
  const suggestions = recentSearches.filter(search => 
    search.toLowerCase().includes(query.toLowerCase())
  );
  
  // Add common search patterns
  const commonPatterns = [
    'active users',
    'pending bookings',
    'available vehicles',
    'verified drivers',
    'recent bookings',
    'suspended accounts',
    'overdue payments'
  ];
  
  const matchingPatterns = commonPatterns.filter(pattern =>
    pattern.toLowerCase().includes(query.toLowerCase())
  );
  
  return [...suggestions, ...matchingPatterns].slice(0, 5);
};